import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { 
  FiMessageSquare,
  FiArrowLeft,
  FiCheck,
  FiZap,
  FiUsers,
  FiCpu,
  FiClock,
  FiStar,
  FiSend,
  FiBookmark,
  FiShare2,
  FiTrendingUp
} from 'react-icons/fi';

const FirstConversationGuide = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const conversationSteps = [
    {
      step: 1,
      title: 'Start a New Conversation',
      description: 'Create your first AI conversation from the dashboard.',
      icon: FiMessageSquare,
      details: [
        'Click "New Conversation" from your dashboard',
        'Choose a conversation title (optional)',
        'Select the AI model(s) you want to use',
        'Pick a conversation template or start blank',
        'Click "Start Conversation" to begin'
      ],
      tips: 'Start with a single AI model for your first conversation to get familiar with the interface.'
    },
    {
      step: 2,
      title: 'Choose Your AI Model',
      description: 'Select the right AI model for your specific needs and tasks.',
      icon: FiCpu,
      details: [
        'Browse available AI models in the selection panel',
        'Read model descriptions and capabilities',
        'Consider your task type (creative, analytical, coding, etc.)',
        'Start with Claude or GPT-4 for general conversations',
        'You can add more models later during the conversation'
      ],
      tips: 'Different AI models excel at different tasks. GPT-4 is great for general use, Claude for analysis, and Gemini for creative tasks.'
    },
    {
      step: 3,
      title: 'Craft Your First Prompt',
      description: 'Write an effective prompt to get the best responses from AI models.',
      icon: FiSend,
      details: [
        'Be specific and clear about what you want',
        'Provide context if your question needs background',
        'Use examples if you want a specific format',
        'Ask follow-up questions to dive deeper',
        'Try different phrasings to get varied perspectives'
      ],
      tips: 'Good prompts are specific, clear, and provide context. Don\'t be afraid to ask follow-up questions!'
    },
    {
      step: 4,
      title: 'Analyze and Compare',
      description: 'Review AI responses and leverage multiple perspectives for better insights.',
      icon: FiTrendingUp,
      details: [
        'Read through all AI responses carefully',
        'Compare different perspectives and approaches',
        'Ask clarifying questions if needed',
        'Use the comparison view to see differences',
        'Save valuable responses for future reference'
      ],
      tips: 'The real power of Bervin comes from comparing multiple AI perspectives on the same question.'
    }
  ];

  const promptExamples = [
    {
      category: 'Creative Writing',
      prompt: 'Help me write a compelling opening paragraph for a science fiction short story about time travel.',
      models: ['GPT-4', 'Claude', 'Gemini'],
      description: 'Get diverse creative perspectives for storytelling'
    },
    {
      category: 'Business Analysis',
      prompt: 'Analyze the pros and cons of remote work policies for a tech startup with 50 employees.',
      models: ['Claude', 'GPT-4'],
      description: 'Compare analytical frameworks and insights'
    },
    {
      category: 'Problem Solving',
      prompt: 'I need to improve my morning routine to be more productive. What are some evidence-based strategies?',
      models: ['GPT-4', 'Claude', 'Gemini'],
      description: 'Get comprehensive solutions from multiple angles'
    },
    {
      category: 'Learning',
      prompt: 'Explain quantum computing in simple terms, then provide a more technical explanation.',
      models: ['Claude', 'GPT-4'],
      description: 'Get explanations at different complexity levels'
    }
  ];

  const bestPractices = [
    {
      icon: FiZap,
      title: 'Be Specific',
      description: 'Specific questions get better answers. Instead of "Help me write," try "Help me write a professional email to decline a job offer."'
    },
    {
      icon: FiUsers,
      title: 'Use Multiple Models',
      description: 'Different AI models have different strengths. Use multiple models to get diverse perspectives on complex topics.'
    },
    {
      icon: FiBookmark,
      title: 'Save Good Conversations',
      description: 'Bookmark valuable conversations and responses. Build your own knowledge base of useful AI interactions.'
    },
    {
      icon: FiShare2,
      title: 'Share with Your Team',
      description: 'Share interesting conversations with team members. Collaborative AI thinking leads to better outcomes.'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-dark-900 via-dark-800 to-secondary-900 text-white">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          {/* Breadcrumb */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="flex items-center space-x-3 mb-8"
          >
            <Link 
              to="/guide" 
              className="text-gray-400 hover:text-white transition-colors duration-200 flex items-center"
            >
              <FiArrowLeft className="w-4 h-4 mr-2" />
              Guide
            </Link>
            <span className="text-gray-500">/</span>
            <span className="text-secondary-400">First AI Conversation</span>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-secondary-500 to-accent-500 rounded-full mb-8"
            >
              <FiMessageSquare className="w-10 h-10 text-white" />
            </motion.div>
            
            <h1 className="text-5xl md:text-6xl font-display font-bold mb-6">
              <span className="gradient-text">First AI Conversation</span>
            </h1>
            
            <p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
              Learn how to have productive conversations with AI models. This guide will help you 
              craft effective prompts, understand AI responses, and make the most of multiple AI perspectives.
            </p>

            <div className="flex items-center justify-center space-x-6 mt-8">
              <div className="flex items-center space-x-2 text-gray-400">
                <FiClock className="w-5 h-5" />
                <span>15 min read</span>
              </div>
              <div className="flex items-center space-x-2 text-gray-400">
                <FiStar className="w-5 h-5" />
                <span>Getting Started</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Steps Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-display font-bold mb-6 gradient-text">
              Your First Conversation
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Follow these steps to start your first productive AI conversation
            </p>
          </motion.div>

          <div className="space-y-16">
            {conversationSteps.map((step, index) => (
              <motion.div
                key={step.step}
                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.2 * index }}
                className={`flex flex-col ${index % 2 === 1 ? 'lg:flex-row-reverse' : 'lg:flex-row'} items-center gap-12`}
              >
                <div className="flex-1">
                  <div className="flex items-center mb-6">
                    <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-r from-secondary-500 to-accent-500 rounded-full text-xl font-bold text-white mr-4">
                      {step.step}
                    </div>
                    <h3 className="text-3xl font-display font-bold text-white">
                      {step.title}
                    </h3>
                  </div>
                  
                  <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                    {step.description}
                  </p>
                  
                  <ul className="space-y-3 mb-6">
                    {step.details.map((detail, detailIndex) => (
                      <li key={detailIndex} className="flex items-start text-gray-300">
                        <FiCheck className="w-5 h-5 text-green-400 mr-3 flex-shrink-0 mt-0.5" />
                        {detail}
                      </li>
                    ))}
                  </ul>
                  
                  <div className="bg-blue-900/20 border border-blue-500/20 rounded-lg p-4">
                    <div className="flex items-center mb-2">
                      <FiStar className="w-4 h-4 text-blue-400 mr-2" />
                      <span className="text-blue-400 font-semibold">Pro Tip</span>
                    </div>
                    <p className="text-gray-300 text-sm">{step.tips}</p>
                  </div>
                </div>
                
                <div className="flex-1 flex justify-center">
                  <div className="relative">
                    <div className="absolute inset-0 bg-gradient-to-r from-secondary-500/20 to-accent-500/20 rounded-3xl blur-xl"></div>
                    <div className="relative w-80 h-64 bg-dark-800/50 backdrop-blur-sm border border-white/10 rounded-3xl flex items-center justify-center">
                      <step.icon className="w-24 h-24 text-secondary-400" />
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Prompt Examples */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-display font-bold mb-6 gradient-text">
              Example Prompts to Try
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Here are some example prompts to get you started with different types of conversations
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {promptExamples.map((example, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 * index }}
                className="group relative"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-secondary-500/10 to-accent-500/10 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="relative bg-dark-800/50 backdrop-blur-sm border border-white/10 rounded-2xl p-8 hover:border-secondary-500/50 transition-all duration-300">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-display font-bold text-white">
                      {example.category}
                    </h3>
                    <div className="flex space-x-1">
                      {example.models.map((model, modelIndex) => (
                        <span 
                          key={modelIndex}
                          className="px-2 py-1 bg-secondary-500/20 text-secondary-300 text-xs rounded-full"
                        >
                          {model}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <div className="bg-dark-700/50 rounded-lg p-4 mb-4">
                    <p className="text-gray-300 italic">"{example.prompt}"</p>
                  </div>
                  
                  <p className="text-gray-400 text-sm">{example.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Best Practices */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-display font-bold mb-6 gradient-text">
              Best Practices
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Follow these guidelines to get the most out of your AI conversations
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {bestPractices.map((practice, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 * index }}
                className="group relative"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-secondary-500/10 to-accent-500/10 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="relative bg-dark-800/50 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:border-secondary-500/50 transition-all duration-300 text-center">
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-r from-secondary-500 to-accent-500 rounded-full mb-4">
                    <practice.icon className="w-6 h-6 text-white" />
                  </div>
                  
                  <h3 className="text-xl font-display font-bold text-white mb-3">
                    {practice.title}
                  </h3>
                  
                  <p className="text-gray-300 text-sm leading-relaxed">
                    {practice.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.0 }}
            className="bg-gradient-to-r from-dark-800/50 to-secondary-900/50 backdrop-blur-sm border border-white/10 rounded-3xl p-12"
          >
            <h2 className="text-3xl md:text-4xl font-display font-bold mb-6 gradient-text">
              Ready for Your First Conversation?
            </h2>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              You now have everything you need to start productive conversations with AI. 
              Jump in and explore the power of multi-AI perspectives!
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
              <a
                href="https://bervin.appiworks.com/app"
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-4 bg-gradient-to-r from-secondary-500 to-accent-500 rounded-xl font-semibold text-white text-lg shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105"
              >
                Start Your First Conversation
              </a>
              <Link
                to="/guide/team-chat"
                className="px-8 py-4 border border-white/20 rounded-xl font-semibold text-white text-lg hover:bg-white/10 transition-all duration-300"
              >
                Next: Team Chat Guide
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FirstConversationGuide;
